import type { EmailParticipant, Message } from "@onemileauto/shared-types";
import { formatFullTimestamp } from "./format";

/**
 * Compose is a plain-text editor (no rich text — non-technical users don't
 * need formatting controls, and it keeps the send path simple/safe: we
 * never re-embed raw, untrusted inbound HTML into an outbound send). We
 * still send an `html` body alongside `text` so recipients on HTML-only
 * clients get sensible rendering — these helpers build that html from the
 * user's plain text, plus a quoted-original block for reply/forward.
 */

export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export function textToSimpleHtml(text: string): string {
  const trimmed = text.trim();
  if (!trimmed) return "<p></p>";
  const paragraphs = trimmed.split(/\n{2,}/).map((p) => `<p>${escapeHtml(p).replace(/\n/g, "<br>")}</p>`);
  return paragraphs.join("");
}

/** Best-effort plain text extraction from inbound HTML, for quoting a message that only has an htmlBody. */
export function stripHtmlToText(html: string): string {
  return html
    .replace(/<(script|style)[^>]*>[\s\S]*?<\/\1>/gi, " ")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/[ \t]+\n/g, "\n")
    .trim();
}

export function participantLabel(p: EmailParticipant): string {
  return p.name?.trim() || p.email;
}

function dedupeParticipants(list: EmailParticipant[]): EmailParticipant[] {
  const seen = new Set<string>();
  const out: EmailParticipant[] = [];
  for (const p of list) {
    const key = p.email.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(p);
  }
  return out;
}

export type ReplyScope = "sender" | "all";

/**
 * Resolve To/Cc for a reply to one specific message — each message in a
 * thread now carries its own inline Reply/Reply All/Forward (see
 * message-card.tsx), so this always acts on the exact message the user
 * tapped, not necessarily the thread's last message.
 *
 *  - "sender": Reply — addressed only to the message's sender.
 *  - "all": Reply All — sender + every original To/Cc recipient, minus the
 *    mailbox's own address (never add ourselves as a recipient of our own
 *    reply) and minus the sender itself in Cc (it's already in To).
 *
 * Recipient resolution is entirely client-side by design — the server
 * (routes/messages.ts) takes whatever To/Cc the client sends as-is and
 * doesn't re-derive it, so this is the one place that logic needs to live.
 */
export function resolveReplyRecipients(
  scope: ReplyScope,
  message: Message,
  selfEmail: string | undefined,
): { to: EmailParticipant[]; cc: EmailParticipant[] } {
  const self = selfEmail?.toLowerCase();
  const isSelf = (p: EmailParticipant) => Boolean(self) && p.email.toLowerCase() === self;

  if (!isSelf(message.from)) {
    const to = [message.from];
    if (scope === "sender") return { to, cc: [] };
    const fromEmail = message.from.email.toLowerCase();
    const cc = dedupeParticipants([...message.to, ...message.cc]).filter(
      (p) => !isSelf(p) && p.email.toLowerCase() !== fromEmail,
    );
    return { to, cc };
  }

  // Replying to your own message (rare — e.g. the thread's most recent
  // message happens to be one you sent) — there's no "sender" to reply to,
  // so fall back to that message's own recipients as the reply target.
  const fallbackTo = dedupeParticipants(message.to).filter((p) => !isSelf(p));
  const to = fallbackTo.length > 0 ? fallbackTo : [message.from];
  if (scope === "sender") return { to, cc: [] };
  const cc = dedupeParticipants(message.cc).filter((p) => !isSelf(p));
  return { to, cc };
}

export function getPlainBody(message: Message): string {
  if (message.textBody?.trim()) return message.textBody;
  if (message.htmlBody) {
    const stripped = stripHtmlToText(message.htmlBody);
    if (stripped) return stripped;
  }
  return message.snippet;
}

function quoteHeaderLines(mode: "reply" | "forward", parent: Message): string[] {
  if (mode === "reply") {
    return [`On ${formatFullTimestamp(parent.createdAt)}, ${participantLabel(parent.from)} wrote:`];
  }
  return [
    "---------- Forwarded message ----------",
    `From: ${participantLabel(parent.from)} <${parent.from.email}>`,
    `Date: ${formatFullTimestamp(parent.createdAt)}`,
    `Subject: ${parent.subject || "(no subject)"}`,
    `To: ${parent.to.map((p) => `${participantLabel(p)} <${p.email}>`).join(", ") || "—"}`,
  ];
}

export interface OutboundBody {
  text: string;
  html: string;
}

/**
 * Compose the final text/html bodies at submit time: the user's own text on
 * top, and — for reply/forward — the quoted original below a visual divider
 * (a top border + indented, muted styling in the html version; a plain
 * "wrote:" header + the original text in the plain-text version).
 */
export function buildOutboundBody(mode: "new" | "reply" | "forward", userText: string, parent: Message | null): OutboundBody {
  const trimmedUser = userText.trim();
  if (mode === "new" || !parent) {
    return { text: trimmedUser, html: textToSimpleHtml(trimmedUser) };
  }

  const headerLines = quoteHeaderLines(mode, parent);
  const quotedPlain = getPlainBody(parent)
    .split("\n")
    .map((line) => `> ${line}`)
    .join("\n");

  const text = [trimmedUser, "", ...headerLines, quotedPlain].join("\n");

  const html = `${textToSimpleHtml(trimmedUser)}<div style="margin-top:20px;padding-top:12px;border-top:2px solid #d9c9ae;color:#6b5d4f;font-size:13px;line-height:1.5;">${headerLines
    .map(escapeHtml)
    .join("<br>")}</div><blockquote style="margin:10px 0 0;padding-left:14px;border-left:3px solid #d9c9ae;color:#3a2f26;">${escapeHtml(
    getPlainBody(parent),
  ).replace(/\n/g, "<br>")}</blockquote>`;

  return { text, html };
}

/**
 * Initial compose body for a brand-new message from a mailbox that has a
 * signature set (Phase 9) — prefilled visibly and editably into the body
 * textarea itself, not hidden behind a toggle or silently appended at send
 * time, matching how quoted reply text is always a visible block (see
 * buildOutboundBody above). Two leading blank lines put the cursor's
 * natural landing spot above the signature, same convention most mail
 * clients use for a bottom-of-message signature.
 *
 * Deliberately "new" mode only — reply/forward don't get this treatment.
 * Re-appending a signature on every reply in a growing thread would look
 * like noise (the signature from the FIRST message in the thread is
 * already there, quoted, further down), and "new message" is the
 * unambiguous case the product ask called out explicitly.
 */
export function buildInitialBody(mode: "new" | "reply" | "forward", signature: string | null | undefined): string {
  if (mode !== "new" || !signature?.trim()) return "";
  return `\n\n${signature.trim()}`;
}

export function ensureSubjectPrefix(subject: string, prefix: "Re: " | "Fwd: "): string {
  const s = subject.trim() || "(no subject)";
  return new RegExp(`^${prefix.trim()}`, "i").test(s) ? s : `${prefix}${s}`;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidEmail(value: string): boolean {
  return EMAIL_RE.test(value.trim());
}
